import React, { useEffect, useRef, useState, useCallback } from 'react';
import { FlatList, useWindowDimensions, Text, View } from 'react-native';
import { Button, Snackbar } from 'react-native-paper';
import { useForm, FormProvider } from 'react-hook-form';

import Question from './Question';
import StepFormNavigation from '../components/StepFormNavigation';
import createStyles, { FORM_MAX_WIDTH } from './style';
import { updateQuestionnaireFlow } from './hooks/useFlowHook';
import { shallowEqual } from 'react-redux';
import { IQuestionItem, IQuestionnaire } from '@nocode/types';
import { isCanvas } from '@common/utils';
import PreviewCms from './PreviewCms';
import useQuestionnaire, {
  useProcessQuestions,
  DISPLAY_QUESTIONS,
} from './hooks/hooks';
import QuestionHeader from './QuestionHeader';
import { getFormControlType } from './helpers';
import {
  COVER_IMAGE_CROPPING,
  COVER_IMAGE_TYPE,
} from '@nocode/types/questionnaire.type';

const Questionnaire: React.FC<IQuestionnaire> = (props) => {
  const {
    configs,
    defaultQuestions,
    style: styleConfig,
    isPreview,
    questions,
    id: formId,
  } = props;
  const methods = useForm();
  const flatlist = useRef<FlatList<any>>(null);
  const canvas = isCanvas();
  const styleSheet = createStyles(styleConfig);

  let { height, width } = useWindowDimensions();
  width = canvas ? 375 : width > FORM_MAX_WIDTH ? FORM_MAX_WIDTH : width;

  const [data, setData] = useState<IQuestionItem[]>([]);
  const [processedQuestions, setProcessedQuestions] = useState<
    Record<string, unknown>[]
  >([]);

  const {
    currentIndex,
    isSubmitting,
    snackbar,
    hideSnackbar,
    onNextButtonClick,
    onPrevButtonClick,
    onSubmit,
    formInstanceId,
  } = useQuestionnaire({ configs, methods, props, filteredData: data });

  const formValuesRef = useRef({});

  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialScrollRef = useRef(true);

  useEffect(() => {
    if (canvas && defaultQuestions) {
      let displayQuestion = defaultQuestions?.[0];

      if (configs.activeChildComponent) {
        const questionPool = [...defaultQuestions, ...DISPLAY_QUESTIONS];
        const control = getFormControlType(configs.activeChildComponent);

        const selected = questionPool.find(
          (question) => question.control === control
        );

        if (selected) {
          displayQuestion = selected;
        }
      }

      setData([
        {
          ...displayQuestion,
          ...(configs.activeChildComponent === 'coverImage'
            ? {
                coverImage: {
                  enabled: true,
                  type: COVER_IMAGE_TYPE.IMAGE,
                  url: 'https://mikosea.s3.ap-northeast-1.amazonaws.com/common/green_coffe_mug.jpg',
                  cropping: COVER_IMAGE_CROPPING.COVER,
                },
              }
            : {}),
          note: {
            enabled: true,
            text: 'この質問はプレビューのみです。このテキストはCMSで自由に非表示にしたり編集したりできます。',
          },
        },
        ...defaultQuestions,
      ]);
    }
  }, [configs.activeChildComponent]);

  useEffect(() => {
    if (!flatlist?.current) return;

    flatlist.current.scrollToOffset({
      animated: true,
      offset: width * currentIndex,
    });
    if (isInitialScrollRef.current) {
      isInitialScrollRef.current = false;
    }
  }, [currentIndex, width, formInstanceId, formId]);

  // Reset scroll position and initial scroll flag when form ID changes
  useEffect(() => {
    isInitialScrollRef.current = true;
    if (flatlist?.current) {
      flatlist.current.scrollToOffset({
        animated: false,
        offset: 0,
      });
    }
  }, [formId, formInstanceId]);

  useEffect(() => {
    if (!canvas && questions) {
      // Process the questions and set up default values
      const processed = useProcessQuestions(questions);
      setProcessedQuestions(processed);
      const defaultValues = questions.reduce((acc: any, question: any) => {
        acc[question.fid || `question-${question.id}`] =
          question.defaultValue || '';
        return acc;
      }, {});
      methods.reset(defaultValues);
      formValuesRef.current = defaultValues;
      const filteredQuestions = updateQuestionnaireFlow(
        processed,
        defaultValues
      );
      setData(filteredQuestions);
    }
  }, [questions, canvas, formId]);

  const updateQuestionnaireData = useCallback(
    (formValues: any) => {
      if (!processedQuestions.length || canvas) return;

      if (shallowEqual(formValuesRef.current, formValues)) return;
      formValuesRef.current = { ...formValues };
      const filteredQuestions = updateQuestionnaireFlow(
        processedQuestions,
        formValues
      );
      setData(filteredQuestions);

      if (
        currentIndex >= filteredQuestions.length &&
        filteredQuestions.length > 0
      ) {
        const newIndex = Math.max(0, filteredQuestions.length - 1);

        if (flatlist?.current) {
          flatlist.current.scrollToOffset({
            animated: true,
            offset: width * newIndex,
          });
        }
      }
    },
    [processedQuestions, canvas, currentIndex, width, data.length]
  );

  useEffect(() => {
    if (!canvas) {
      const subscription = methods.watch((formValues) => {
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }
        debounceTimeoutRef.current = setTimeout(() => {
          updateQuestionnaireData(formValues);
        }, 100);
      });

      return () => {
        subscription.unsubscribe();
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }
      };
    }
  }, [updateQuestionnaireData, canvas]);

  if (isPreview) {
    return <PreviewCms {...props} />;
  }

  return (
    <FormProvider {...methods}>
      <View
        style={[
          styleSheet.container,
          {
            width,
          },
        ]}
      >
        {data && data.length > 0 && configs.formType === 'step-form' ? (
          <QuestionHeader
            canvas={canvas}
            currentIndex={currentIndex}
            questions={data as unknown as IQuestionItem[]}
            styleConfig={styleConfig}
            configs={configs}
          />
        ) : null}

        {canvas ? (
          <Question
            item={data[0]}
            width={width}
            formType={configs.formType}
            styleConfig={styleConfig}
            index={0}
          />
        ) : (
          <FlatList
            data={canvas ? data.slice(0, 1) : data}
            ref={flatlist}
            renderItem={({ item, index }) => (
              <Question
                item={item}
                index={index}
                questionMarker={configs.questionMarker}
                formType={configs.formType}
                styleConfig={styleConfig}
                width={width}
              />
            )}
            snapToAlignment="start"
            keyExtractor={(item, index) =>
              item?.id?.toString() || index.toString()
            }
            {...(configs.formType === 'step-form'
              ? {
                  getItemLayout: (_data, index) => ({
                    length: width,
                    offset: width * index,
                    index,
                  }),
                  horizontal: true,
                  pagingEnabled: true,
                  initialScrollIndex: 0,
                  showsHorizontalScrollIndicator: false,
                }
              : {
                  initialScrollIndex: 0,
                  scrollEnabled: true,
                  getItemLayout: (_data, index) => ({
                    length: width,
                    offset: height * index,
                    index,
                  }),
                })}
            style={{
              width,
              maxWidth: FORM_MAX_WIDTH,
            }}
          />
        )}

        <View
          style={{
            display: 'flex',
            width: '100%',
            flexDirection: 'row',
            paddingHorizontal: 20,
            justifyContent: 'center',
            marginTop: 40,
          }}
        >
          {data &&
            data.length > 0 &&
            (configs.formType === 'step-form' ? (
              <StepFormNavigation
                onNextButtonClick={onNextButtonClick}
                onPrevButtonClick={onPrevButtonClick}
                styles={styleSheet}
                disabledBack={isSubmitting || canvas || currentIndex === 0}
                disabledForward={isSubmitting || canvas}
                isSubmitting={isSubmitting}
              />
            ) : (
              <Button
                style={[
                  styleSheet.commonButtonWrapper,
                  styleSheet.submitButton,
                  styleSheet.nextButtonWrapper,
                ]}
                contentStyle={styleSheet.commonButtonContent}
                labelStyle={[
                  styleSheet.commonButtonLabel,
                  styleSheet.nextButtonLabel,
                ]}
                onPress={methods.handleSubmit(onSubmit)}
                disabled={isSubmitting || canvas}
                loading={isSubmitting}
              >
                Submit
              </Button>
            ))}
        </View>
      </View>

      <Snackbar
        visible={snackbar.visible}
        onDismiss={hideSnackbar}
        duration={snackbar.duration}
        style={snackbar.style}
        action={
          snackbar.showAction
            ? {
                label: '閉じる',
                onPress: hideSnackbar,
              }
            : undefined
        }
      >
        {snackbar.message}
      </Snackbar>
    </FormProvider>
  );
};

export default Questionnaire;
